import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft } from 'lucide-react';
import axiosInstance from '../utils/axiosConfig';
import TrDateInput from '../components/TrDateInput';

interface Cari {
  cariID: number;
  adiSoyadi: string;
  firmaAdi: string | null;
  telefon1: string;
  telefon2: string | null;
  ePosta: string | null;
  adres: string | null;
}

interface SunumDaire {
  sunumDaireID: number;
  daireID: number;
  daire: {
    daireID: number;
    daireNo: string;
    projeAdi: string | null;
    blokAdi: string | null;
    katAdi: string | null;
    cepheAdi: string | null;
    daireTipiAdi: string | null;
    listeFiyati: number | null;
    netM2: number | null;
    brutM2: number | null;
    daireDurumu: string;
  } | null;
}

interface Sunum {
  sunumID: number;
  cari: Cari | null;
  daireler: SunumDaire[];
}

interface Teklif {
  teklifID: number;
  teklifAdi: string;
  gayrimenkulID: number;
  teklifMiktari: number | null;
  ortalamaVadeGun: number | null;
  kalemler: { kalemID: number | null; vadeTarihi: string; miktar: number; aciklama: string | null }[];
}

interface TaksitSatir {
  vadeTarihi: string;
  miktar: string;
  aciklama: string;
  odemeTuru: string;
}

const ODEME_TURLERI = [
  { value: 'Nakit', label: 'Nakit' },
  { value: 'BankaHavale', label: 'Banka Havale/EFT' },
  { value: 'Cek', label: 'Çek' },
  { value: 'Senet', label: 'Senet' },
  { value: 'Takas', label: 'Takas' },
  { value: 'Mahsup', label: 'Mahsup (Daire Karşılığı)' },
];

const formatCurrency = (value: string): string => {
  const num = value.replace(/[^\d]/g, '');
  if (!num) return '';
  return parseInt(num).toLocaleString('tr-TR');
};
const parseCurrency = (value: string): string => value.replace(/[^\d]/g, '');

const TeklifSozlesme: React.FC = () => {
  const { id, teklifId } = useParams<{ id: string; teklifId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [sunum, setSunum] = useState<Sunum | null>(null);
  const [teklif, setTeklif] = useState<Teklif | null>(null);
  const [satisFiyati, setSatisFiyati] = useState('');
  const [sozlesmeTarihi, setSozlesmeTarihi] = useState(new Date().toISOString().slice(0, 10));
  const [sozlesmeDurumu, setSozlesmeDurumu] = useState<'Sözleşme' | 'Rezerve'>('Sözleşme');
  const [rezerveBitisTarihi, setRezerveBitisTarihi] = useState('');
  const [ekSartlar, setEkSartlar] = useState('');
  const [kalemler, setKalemler] = useState<TaksitSatir[]>([]);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sunumRes, teklifRes] = await Promise.all([
        axiosInstance.get(`/api/MusteriSunumlari/${id}`),
        axiosInstance.get(`/api/SunumGeciTeklifler/${teklifId}`),
      ]);
      setSunum(sunumRes.data);
      setTeklif(teklifRes.data);
      setSatisFiyati(teklifRes.data.teklifMiktari != null ? String(teklifRes.data.teklifMiktari) : '');
      setKalemler((teklifRes.data.kalemler || []).map((k: any) => ({
        vadeTarihi: k.vadeTarihi,
        miktar: String(k.miktar),
        aciklama: k.aciklama || '',
        odemeTuru: 'Nakit',
      })));
    } catch {
      toast.error('Veriler yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, [id, teklifId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (sozlesmeDurumu === 'Rezerve' && !rezerveBitisTarihi) {
      const d = new Date();
      d.setDate(d.getDate() + 3);
      setRezerveBitisTarihi(d.toISOString().slice(0, 10));
    }
  }, [sozlesmeDurumu, rezerveBitisTarihi]);

  const sd = sunum?.daireler.find(d => d.daireID === teklif?.gayrimenkulID) || null;
  const daireUygun = sd?.daire?.daireDurumu === 'Satışta';

  const toplamKalem = kalemler.reduce((s, k) => s + (parseFloat(k.miktar) || 0), 0);
  const satisFiyatiNum = parseFloat(satisFiyati.replace(/[^\d.]/g, '')) || 0;
  const fark = satisFiyatiNum - toplamKalem;

  const handleKalemGuncelle = (i: number, patch: Partial<TaksitSatir>) => {
    setKalemler(prev => prev.map((k, idx) => idx === i ? { ...k, ...patch } : k));
  };

  const handleKalemSil = (i: number) => {
    setKalemler(prev => prev.filter((_, idx) => idx !== i));
  };

  const handleKalemEkle = () => {
    setKalemler(prev => [...prev, { vadeTarihi: '', miktar: '', aciklama: '', odemeTuru: 'Nakit' }]);
  };

  const handleSubmit = async () => {
    if (!sunum || !teklif || !sd?.daire) return;
    if (!daireUygun) {
      toast.error('Bu daire artık "Satışta" durumunda değil, sözleşme oluşturulamaz.');
      return;
    }
    if (!sunum.cari) {
      toast.error('Bu sunuma bağlı müşteri bulunamadı.');
      return;
    }
    if (!satisFiyatiNum) {
      toast.error('Satış fiyatı girilmelidir.');
      return;
    }
    if (kalemler.length === 0) {
      toast.error('En az bir ödeme kalemi girilmelidir.');
      return;
    }
    if (kalemler.some(k => !k.vadeTarihi || !(parseFloat(k.miktar) > 0))) {
      toast.error('Tüm ödeme kalemlerinin vade tarihi ve miktarı dolu olmalıdır.');
      return;
    }
    setSaving(true);
    try {
      const sozRes = await axiosInstance.post('/api/Sozlesmeler', {
        DaireID: sd.daire.daireID,
        CariID: sunum.cari.cariID,
        SozlesmeTarihi: sozlesmeTarihi,
        RezerveBitisTarihi: sozlesmeDurumu === 'Rezerve' ? rezerveBitisTarihi : null,
        SozlesmeDurumu: sozlesmeDurumu,
        SozlesmeFiyati: satisFiyatiNum,
        PlanlananOdeme: 0,
        OrtalamaVade: 0,
        OzelSartlar: ekSartlar || null,
      });
      const sozlesmeID = sozRes.data.sozlesmeID;
      const sozlesmeTakipNo = sozRes.data.sozlesmeTakipNo;

      const taksitler = kalemler.map((k, i) => ({
        vadeTarihi: k.vadeTarihi,
        miktar: parseFloat(k.miktar) || 0,
        odemeTuruDetay: k.aciklama || 'Taksit',
        odemeTuru: k.odemeTuru,
        aciklama: k.aciklama || null,
        siraNo: i,
      }));
      await axiosInstance.post('/api/OdemePlanTaksitleri/toplu', {
        sozlesmeID,
        cariID: sunum.cari.cariID,
        temizleMevcutPlan: true,
        taksitler,
      });

      await axiosInstance.put(`/api/MusteriSunumlari/${sunum.sunumID}`, { durum: 'Olumlu Sonuçlandı' });

      toast.success('Sözleşme oluşturuldu.');
      navigate(`/sozlesme-detay/${sozlesmeTakipNo}`);
    } catch {
      toast.error('Sözleşme oluşturulurken hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  const card: React.CSSProperties = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, boxShadow: '0 1px 2px rgba(0,0,0,0.04)', padding: 20 };
  const lbl: React.CSSProperties = { display: 'block', fontSize: 10.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 };
  const inp: React.CSSProperties = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13.5, outline: 'none', boxSizing: 'border-box' };

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8' }}>
        <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>Yükleniyor...
      </div>
    );
  }

  if (!sunum || !teklif || !sd?.daire) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8' }}>
        Teklif veya sunum bulunamadı.
      </div>
    );
  }

  return (
    <div style={{ padding: '28px 32px', background: '#f4f6fb', minHeight: '100vh', fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 22 }}>
        <button onClick={() => navigate(`/musteri-sunumlari/${id}`)}
          style={{ width: 38, height: 38, borderRadius: 10, background: '#fff', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
          <ArrowLeft size={16} />
        </button>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: '#1a2440', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <i className="fas fa-file-signature" style={{ color: '#e67d21', fontSize: 18 }}></i>
        </div>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Sözleşme Yap — Daire {sd.daire.daireNo}</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>Teklif: {teklif.teklifAdi}</div>
        </div>
      </div>

      {!daireUygun && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: '14px 18px', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 10, color: '#b91c1c', fontSize: 13, fontWeight: 600 }}>
          <i className="fas fa-exclamation-triangle"></i>
          Bu daire şu anda "{sd.daire.daireDurumu}" durumunda — sadece "Satışta" olan daireler için sözleşme oluşturulabilir.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 900 }}>
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
          <div style={{ ...card, flex: '1 1 320px' }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <i className="fas fa-user" style={{ color: '#3b82f6' }}></i>Müşteri Bilgileri
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1a2440' }}>{sunum.cari?.adiSoyadi || '—'}</div>
            {sunum.cari?.firmaAdi && <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 2 }}>{sunum.cari.firmaAdi}</div>}
            <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 6 }}>{sunum.cari?.telefon1 || '—'}</div>
            {sunum.cari?.ePosta && <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 2 }}>{sunum.cari.ePosta}</div>}
          </div>

          <div style={{ ...card, flex: '1 1 320px' }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <i className="fas fa-door-open" style={{ color: '#e67d21' }}></i>Daire Bilgileri
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1a2440' }}>
              {[sd.daire.projeAdi, sd.daire.blokAdi].filter(Boolean).join(' — ')}
            </div>
            <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 6 }}>
              Daire {sd.daire.daireNo} · {sd.daire.katAdi || '—'} · {sd.daire.cepheAdi || '—'}
            </div>
            <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 2 }}>
              {sd.daire.daireTipiAdi || '—'} · Net {sd.daire.netM2 ?? '—'} m² / Brüt {sd.daire.brutM2 ?? '—'} m²
            </div>
            <div style={{ marginTop: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20, background: daireUygun ? '#dcfce7' : '#fee2e2', color: daireUygun ? '#15803d' : '#b91c1c' }}>
                {sd.daire.daireDurumu}
              </span>
            </div>
          </div>
        </div>

        <div style={card}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <i className="fas fa-file-contract" style={{ color: '#1a2440' }}></i>Sözleşme Bilgileri
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {(['Sözleşme', 'Rezerve'] as const).map(d => (
              <button key={d} onClick={() => setSozlesmeDurumu(d)}
                style={{ flex: 1, padding: '9px 0', borderRadius: 9, border: sozlesmeDurumu === d ? '1.5px solid #1a2440' : '1.5px solid #e5e7eb', background: sozlesmeDurumu === d ? '#1a2440' : '#fff', color: sozlesmeDurumu === d ? '#fff' : '#64748b', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                {d}
              </button>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: sozlesmeDurumu === 'Rezerve' ? '1fr 1fr 1fr' : '1fr 1fr', gap: 14, marginBottom: 16 }}>
            <div>
              <label style={lbl}>Sözleşme Tarihi</label>
              <TrDateInput value={sozlesmeTarihi} onChange={e => setSozlesmeTarihi(e.target.value)} style={inp} />
            </div>
            {sozlesmeDurumu === 'Rezerve' && (
              <div>
                <label style={lbl}>Rezerve Bitiş Tarihi</label>
                <TrDateInput value={rezerveBitisTarihi} onChange={e => setRezerveBitisTarihi(e.target.value)} style={inp} />
              </div>
            )}
            <div>
              <label style={lbl}>Satış Fiyatı (₺)</label>
              <input type="text" value={formatCurrency(satisFiyati)} onChange={e => setSatisFiyati(parseCurrency(e.target.value))} style={{ ...inp, fontWeight: 700, color: '#e67d21' }} />
            </div>
          </div>
          <div>
            <label style={lbl}>Ek Şartlar</label>
            <textarea value={ekSartlar} onChange={e => setEkSartlar(e.target.value)} rows={3}
              placeholder="Sözleşmeye eklenecek özel şartlar..."
              style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
        </div>

        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="fas fa-list-ul" style={{ color: '#3b82f6' }}></i>Ödeme Planı
            </div>
            <div style={{ fontSize: 11.5, fontWeight: 700, padding: '5px 10px', borderRadius: 8, background: fark === 0 ? '#dcfce7' : '#fef3c7', color: fark === 0 ? '#059669' : '#d97706' }}>
              Toplam: {toplamKalem.toLocaleString('tr-TR')} ₺ / Fark: {fark >= 0 ? '+' : ''}{fark.toLocaleString('tr-TR')} ₺
            </div>
          </div>
          {kalemler.length > 0 && (
            <div style={{ border: '1px solid #f1f5f9', borderRadius: 12, overflow: 'hidden', marginBottom: 12 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={{ padding: '9px 10px', textAlign: 'left', fontWeight: 700, color: '#94a3b8', fontSize: 10.5, textTransform: 'uppercase' }}>#</th>
                    <th style={{ padding: '9px 10px', textAlign: 'left', fontWeight: 700, color: '#94a3b8', fontSize: 10.5, textTransform: 'uppercase' }}>Vade Tarihi</th>
                    <th style={{ padding: '9px 10px', textAlign: 'left', fontWeight: 700, color: '#94a3b8', fontSize: 10.5, textTransform: 'uppercase' }}>Miktar (₺)</th>
                    <th style={{ padding: '9px 10px', textAlign: 'left', fontWeight: 700, color: '#94a3b8', fontSize: 10.5, textTransform: 'uppercase' }}>Ödeme Türü</th>
                    <th style={{ padding: '9px 10px', textAlign: 'left', fontWeight: 700, color: '#94a3b8', fontSize: 10.5, textTransform: 'uppercase' }}>Açıklama</th>
                    <th style={{ padding: '9px 4px', width: 32 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {kalemler.map((k, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fbfbfd', borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '7px 10px', color: '#cbd5e1', fontWeight: 700 }}>{i + 1}</td>
                      <td style={{ padding: '5px 6px' }}>
                        <TrDateInput value={k.vadeTarihi} onChange={e => handleKalemGuncelle(i, { vadeTarihi: e.target.value })}
                          style={{ padding: '6px 9px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 12.5, width: '100%' }} />
                      </td>
                      <td style={{ padding: '5px 6px' }}>
                        <input type="text" value={formatCurrency(k.miktar)} onChange={e => handleKalemGuncelle(i, { miktar: parseCurrency(e.target.value) })}
                          style={{ padding: '6px 9px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 12.5, width: '100%', boxSizing: 'border-box' }} />
                      </td>
                      <td style={{ padding: '5px 6px' }}>
                        <select value={k.odemeTuru} onChange={e => handleKalemGuncelle(i, { odemeTuru: e.target.value })}
                          style={{ padding: '6px 9px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 12.5, width: '100%', background: '#fff' }}>
                          {ODEME_TURLERI.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '5px 6px' }}>
                        <input type="text" value={k.aciklama} onChange={e => handleKalemGuncelle(i, { aciklama: e.target.value })} placeholder="—"
                          style={{ padding: '6px 9px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 12.5, width: '100%', boxSizing: 'border-box' }} />
                      </td>
                      <td style={{ padding: '5px 6px', textAlign: 'center' }}>
                        <button onClick={() => handleKalemSil(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', padding: 4 }}
                          onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')} onMouseLeave={e => (e.currentTarget.style.color = '#cbd5e1')}>
                          <i className="fas fa-times" style={{ fontSize: 13 }}></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <button onClick={handleKalemEkle}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', border: '1.5px dashed #cbd5e1', borderRadius: 9, background: 'transparent', fontSize: 12.5, color: '#64748b', cursor: 'pointer', fontWeight: 600 }}>
            <i className="fas fa-plus" style={{ fontSize: 11 }}></i> Kalem Ekle
          </button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingBottom: 20 }}>
          <button onClick={() => navigate(`/musteri-sunumlari/${id}`)}
            style={{ padding: '10px 20px', border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff', fontSize: 13.5, fontWeight: 600, color: '#475569', cursor: 'pointer' }}>
            İptal
          </button>
          <button onClick={handleSubmit} disabled={saving || !daireUygun}
            style={{ padding: '10px 24px', border: 'none', borderRadius: 10, background: (saving || !daireUygun) ? '#94a3b8' : '#059669', color: '#fff', fontSize: 13.5, fontWeight: 700, cursor: (saving || !daireUygun) ? 'not-allowed' : 'pointer' }}>
            <i className="fas fa-check" style={{ marginRight: 6 }}></i>{saving ? 'Kaydediliyor...' : 'Sözleşme Yap'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeklifSozlesme;
